import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

type RouteModule = Record<string, unknown>;
export type RouteModuleLoader = () => Promise<RouteModule>;

const RELOAD_GUARD_PREFIX = 'rconf:chunk-reload:';
const RELOAD_GUARD_TTL = 60_000;
const inMemoryReloads = new Set<string>();
const prefetchedChunks = new Map<string, Promise<unknown>>();

export function lazyNamedRoute(
  loader: RouteModuleLoader,
  exportName: string,
  chunkName: string,
): LazyExoticComponent<ComponentType<any>> {
  return lazy(async () => {
    const module = await loadRouteModule(loader, chunkName);
    const component = module[exportName];
    if (!component) throw new Error(`Route export ${exportName} is missing in ${chunkName}`);
    return { default: component as ComponentType<any> };
  });
}

export function prefetchRouteModule(loader: RouteModuleLoader, chunkName: string): Promise<unknown> {
  const current = prefetchedChunks.get(chunkName);
  if (current) return current;

  const pending = loader()
    .then((module) => {
      clearReloadGuard(chunkName);
      return module;
    })
    .catch((error) => {
      prefetchedChunks.delete(chunkName);
      throw error;
    });
  prefetchedChunks.set(chunkName, pending);
  return pending;
}

async function loadRouteModule(loader: RouteModuleLoader, chunkName: string): Promise<RouteModule> {
  try {
    const module = await loader();
    clearReloadGuard(chunkName);
    return module;
  } catch (error) {
    if (!isChunkLoadError(error) || typeof window === 'undefined' || recentlyReloaded(chunkName)) throw error;
    markReload(chunkName);
    window.location.reload();
    return new Promise<RouteModule>(() => undefined);
  }
}

export function isChunkLoadError(error: unknown): boolean {
  const value = error instanceof Error ? `${error.name} ${error.message}` : String(error || '');
  return /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(value);
}

function guardKey(chunkName: string) {
  return `${RELOAD_GUARD_PREFIX}${chunkName}`;
}

function recentlyReloaded(chunkName: string): boolean {
  if (inMemoryReloads.has(chunkName)) return true;
  const key = guardKey(chunkName);
  const historyMark = Number((window.history.state as Record<string, unknown> | null)?.[key] || 0);
  let storageMark = 0;
  try { storageMark = Number(window.sessionStorage.getItem(key) || 0); } catch { /* history state is the fallback */ }
  return Date.now() - Math.max(historyMark, storageMark) < RELOAD_GUARD_TTL;
}

function markReload(chunkName: string) {
  const key = guardKey(chunkName);
  const timestamp = Date.now();
  inMemoryReloads.add(chunkName);
  try { window.sessionStorage.setItem(key, String(timestamp)); } catch { /* history state is the fallback */ }
  try { window.history.replaceState({ ...(window.history.state || {}), [key]: timestamp }, ''); } catch { /* memory guard still prevents a loop */ }
}

function clearReloadGuard(chunkName: string) {
  if (typeof window === 'undefined') return;
  const key = guardKey(chunkName);
  inMemoryReloads.delete(chunkName);
  try { window.sessionStorage.removeItem(key); } catch { /* no-op */ }
  try {
    const state = { ...(window.history.state || {}) };
    if (key in state) {
      delete state[key];
      window.history.replaceState(state, '');
    }
  } catch { /* no-op */ }
}
